import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Layers, Search } from 'lucide-react';
import { lessonsIndex, loadLessons, totalLessonVocabCount } from '../data/lessons';
import { loadWordlists, wordlistsIndex, totalWordlistVocabCount } from '../data/wordlists';
import { Footer } from '../components/Footer';
import { HomeHeader } from '../components/Header';
import { LessonProgressDot } from '../components/LessonProgressDot';
import { vars } from '../styles/theme.css';
import { text } from '../styles/text.css';

type Tab = 'lessons' | 'wordlists';

// Module-level so the active tab survives navigation away and back.
// Resets on full page reload (intentional).
let lastTab: Tab = 'lessons';

export function LessonsListPage() {
  const [search, setSearch] = useState('');
  const [tab, setTabState] = useState<Tab>(lastTab);
  const setTab = (t: Tab) => {
    lastTab = t;
    setTabState(t);
  };

  // Eagerly start fetching the full lesson + wordlist bundles in the background
  // while the user is on the home page. The detail / flashcards routes await
  // the same module-cached promise, so by the time they click in, the data is
  // either already in memory or in-flight.
  useEffect(() => {
    void loadLessons();
    void loadWordlists();
  }, []);

  const items = tab === 'lessons' ? lessonsIndex : wordlistsIndex;
  const basePath = tab === 'lessons' ? '/lessons' : '/wordlists';
  const itemNoun = tab === 'lessons' ? 'lesson' : 'word list';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        (l.scenarioSummary?.toLowerCase().includes(q) ?? false) ||
        l.category.toLowerCase().includes(q) ||
        l.vocabAreas.some((v) => v.toLowerCase().includes(q)),
    );
  }, [search, items]);

  const totalVocab = totalLessonVocabCount + totalWordlistVocabCount;
  const totalContent = lessonsIndex.length + wordlistsIndex.length;

  return (
    <>
      <HomeHeader />
      <div className="max-w-reading mx-auto px-4 pt-5 pb-32">
        {/* Tagline */}
        <div className="mb-5">
          <h1
            className={text.hero}
            style={{ color: vars.color.ink, marginBottom: 6 }}
          >
            Real Japanese
            <br />
            <span style={{ color: vars.color.inkMuted }}>for real situations.</span>
          </h1>
          <p className={text.body} style={{ color: vars.color.inkMuted }}>
            Tap any line to hear it spoken.
          </p>
        </div>

        {/* Practice card */}
        <Link
          to="/practice"
          className="w-full mb-5 p-3.5 flex items-center justify-between transition-all group active:scale-[0.99]"
          style={{
            backgroundColor: vars.color.surface,
            color: vars.color.ink,
            border: `1.5px solid ${vars.color.accent}`,
            borderRadius: 12,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: vars.color.accent,
                color: vars.color.onAccent,
              }}
            >
              <Layers size={16} strokeWidth={2} />
            </div>
            <div className="text-left">
              <div className={text.body} style={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                Random practice
              </div>
              <div className={text.caption} style={{ color: vars.color.inkMuted }}>
                20 cards from {totalVocab.toLocaleString()} across {totalContent} {totalContent === 1 ? 'item' : 'items'}
              </div>
            </div>
          </div>
          <ChevronRight
            size={18}
            strokeWidth={2}
            className="group-hover:translate-x-0.5 transition-all"
            style={{ color: vars.color.accent }}
          />
        </Link>

        {/* Tabs */}
        <div
          className="mb-3 flex p-0.5"
          role="tablist"
          aria-label="Content type"
          style={{
            backgroundColor: vars.color.surface,
            border: `1px solid ${vars.color.border}`,
            borderRadius: 10,
          }}
        >
          {(
            [
              { id: 'lessons' as const, label: 'Lessons' },
              { id: 'wordlists' as const, label: 'Vocabulary' },
            ]
          ).map(({ id, label }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(id)}
                className={`flex-1 py-1.5 transition-colors ${text.meta}`}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  backgroundColor: active ? vars.color.accent : 'transparent',
                  color: active ? vars.color.onAccent : vars.color.inkSoft,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-7">
          <Search
            size={15}
            strokeWidth={2}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: vars.color.inkMuted,
            }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'lessons' ? 'Search lessons' : 'Search word lists'}
            className={`w-full pl-9 pr-3 outline-none transition-colors ${text.body}`}
            style={{
              height: 38,
              backgroundColor: vars.color.surface,
              border: `1px solid ${vars.color.border}`,
              borderRadius: 10,
              color: vars.color.ink,
            }}
          />
        </div>

        {/* Lessons in numeric order */}
        <div
          className="overflow-hidden mb-7"
          style={{
            backgroundColor: vars.color.surface,
            border: `1px solid ${vars.color.border}`,
            borderRadius: 12,
          }}
        >
          {filtered.map((lesson, i) => (
            <Link
              key={lesson.id}
              to={`${basePath}/${lesson.number}`}
              className="w-full text-left transition-colors group flex items-start gap-3 px-3.5 py-3 active:bg-black/5"
              style={{
                borderBottom:
                  i < filtered.length - 1 ? `1px solid ${vars.color.border}` : 'none',
              }}
            >
              <div
                className={`flex-shrink-0 flex items-center justify-center ${text.caption}`}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: 'rgba(192, 60, 47, 0.08)',
                  color: vars.color.accent,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                }}
              >
                {String(lesson.number).padStart(2, '0')}
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <div
                  className={text.tag}
                  style={{
                    color: vars.color.inkFaint,
                    letterSpacing: '0.04em',
                    marginBottom: 1,
                  }}
                >
                  {lesson.category}
                </div>
                <h3 className={text.h3} style={{ color: vars.color.ink }}>
                  {lesson.title}
                </h3>
                {lesson.scenarioSummary && (
                  <p
                    className={text.meta}
                    style={{ color: vars.color.inkMuted, marginTop: 2 }}
                  >
                    {lesson.scenarioSummary}
                  </p>
                )}
              </div>
              <LessonProgressDot
                lessonId={lesson.id}
                totalSections={lesson.sectionsCount}
                style={{ marginTop: 11 }}
              />
              <ChevronRight
                size={16}
                strokeWidth={2}
                className="flex-shrink-0 transition-transform group-hover:translate-x-0.5"
                style={{ color: vars.color.inkFaint, marginTop: 8 }}
              />
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div
            className={`text-center py-12 ${text.body}`}
            style={{ color: vars.color.inkMuted }}
          >
            {search
              ? `No ${itemNoun}s match "${search}"`
              : `No ${itemNoun}s yet.`}
          </div>
        )}

        <div className="text-center mt-10">
          <p
            className={text.tag}
            style={{ color: vars.color.inkFaint, letterSpacing: '0.04em' }}
          >
            {items.length} {itemNoun}{items.length === 1 ? '' : 's'}
          </p>
        </div>

        <Footer />
      </div>
    </>
  );
}
